<template>
  <SoDialog
    v-model="value"
    :loading="isLoading"
    width="600"
    @on-close="$emit('closeModal')"
  >
    <SoErrorBoundary>
      <template v-if="!isLoading && billId">
        <div class="d-flex align-center justify-space-between">
          <h4>Bill: {{ bill.billInfo.juniferBillNumber }}</h4>
          <SoStatus
            :status="{
              type: bill.billInfo.status === 'Accepted' ? 'normal' : 'default',
              text: bill.billInfo.status,
            }"
          />
        </div>
        <SoChip
          v-if="bill.billInfo.isFinalBill"
          :type="SoChipType.IMPORTANT"
          class="mr-1"
          text="Final Bill"
        />
        <SoChip
          v-if="bill.billInfo.isSuperseded"
          :type="SoChipType.IMPORTANT"
          class="mr-1"
          text="Superseded"
        />
        <SoChip
          v-if="bill.billInfo.isDirtyBill"
          :type="SoChipType.IMPORTANT"
          class="mr-1"
          text="Dirty"
        />
        <SoChip
          v-if="bill.billInfo.isConsolidatedBill"
          :type="SoChipType.IMPORTANT"
          class="mr-1"
          text="Consolidated"
        />
        <dl class="mt-5 mb-7 dl dl--aligned">
          <dt>Email Delivery</dt>
          <!-- The bill email is a separate query inside getBill and can fail on
               its own (Junifer returns a BillingErrorResponse), leaving its
               data undefined while the aggregate has stopped fetching. Reading
               through it unguarded threw, and SoErrorBoundary swallowed the
               error, blanking the entire modal. -->
          <dd>{{ bill.billEmail?.deliveryStatus || "-" }}</dd>
          <dt>Bill Period</dt>
          <dd>
            {{ formatDate(bill.billInfo.periodFrom) }} -
            {{ formatDate(bill.billInfo.periodTo) }}
          </dd>
          <dt>Created</dt>
          <dd>{{ formatDate(bill.billInfo.billCreatedDate) }}</dd>
          <dt>Accepted</dt>
          <dd>{{ formatDate(bill.billInfo.billAcceptedDate) }}</dd>
          <dt>Due Date</dt>
          <dd>{{ formatDate(bill.billInfo.dueDate) }}</dd>
        </dl>
        <p class="mb-1 font-weight-medium">Charges on Bill</p>
        <SoDivider />
        <dl class="my-1 dl dl--aligned">
          <template
            v-for="billLine in bill.billBreakdownLines"
            :key="billLine.id"
          >
            <dt>{{ billLine.description }}</dt>
            <dd>{{ formatMoney({ amount: billLine.billAmount }) }}</dd>
          </template>
        </dl>
        <SoDivider />
        <dl class="dl dl--aligned">
          <dt>Total Charges</dt>
          <dd>{{ bill.billInfo.chargesFormatted }}</dd>
        </dl>
        <div class="mt-11 d-flex justify-end">
          <SoButton
            :loading="billImage.isFetching"
            :disabled="billImage.isFetching || billDownloaded"
            :title="billDownloaded ? 'Downloaded' : 'Download Bill'"
            :icon="billDownloaded ? 'check' : undefined"
            type="primary"
            data-testid="download-bill-button"
            @click="downloadBill"
          />
        </div>
        <SoAlert
          v-if="billDownloadError"
          class="mt-4"
          title="Bill download failed. Please retry."
          type="error"
        />
      </template>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { BillService } from "@/services/BillService"
  import { errorReporter } from "@/services/ErrorService"
  import { getBill } from "@/services-handlers/getBill"
  import { formatDate, formatDateDashed } from "@/helpers/dateHelper"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import { downloadFile } from "@/helpers/downloadFile"

  export default defineComponent({
    components: {
      SoDialog,
      SoAlert,
      SoStatus,
      SoButton,
      SoChip,
      SoDivider,
      SoErrorBoundary,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      billId: {
        type: Number,
        default: 0,
      },
    },
    emits: ["update:modelValue", "closeModal"],
    data() {
      return {
        formatDate,
        formatMoney,
        getBill,
        BillService,
        SoChipType,
        current: useCurrentAccountStore(),
        billDownloaded: false,
        billDownloadError: false,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      isLoading() {
        return getBill(this.billId, this.current.coreId.data).isFetching
      },
      bill() {
        return getBill(this.billId, this.current.coreId.data).data
      },
      billImage() {
        return BillService.getBillImage(this.billId)
      },
      billFileName() {
        const { periodFrom, periodTo, juniferBillNumber } = this.bill.billInfo
        const from = periodFrom ? formatDateDashed(periodFrom) : ""
        const to = periodTo ? ` to ${formatDateDashed(periodTo)}` : ""
        const period = from ? ` - ${from}${to}` : ""
        return `Account ${this.current.accountNumber}${period} - Statement ${juniferBillNumber}.pdf`
      },
    },
    methods: {
      async downloadBill() {
        this.billDownloadError = false
        try {
          await BillService.getBillImage(this.billId).refetch()
          downloadFile(
            this.billImage.data,
            this.billFileName,
            "application/pdf",
          )
          this.billDownloaded = true
        } catch (error) {
          this.billDownloadError = true
          errorReporter.report(error)
          console.error(error)
        }
      },
    },
  })
</script>
